import { useEffect, useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  IconExpand,
  IconLoader,
  PermissionDenied,
  PermissionEnum,
} from '@ui/src';
import { useAppSelector } from '@store';
import VolumesRiskTransactionsForm from '../../../components/volumes-risk/transactions/form';
import VolumesRiskTransactionsList from '../../../components/volumes-risk/transactions/list';
import { Factor, RiskScoreCalculationType, RiskScoreType } from '../../types';
import {
  useUpdateFactorMutation,
  useLazyGetFactorsQuery,
} from '../../../store/dictionaries/services';
import {
  useLazyGetAmlBaseCurrencyQuery,
  useLazyGetTransactionVolumesQuery,
} from '../store/services';

import ROUTES from '../../../routes';

import cn from '../transactions-risk.module.css';

const TransactionVolumesRisk = () => {
  const { control, handleSubmit, watch, reset } = useForm<any>({
    defaultValues: {
      individualSingleTranscationVol: '',
      individualMultipleTranscationVol: '',
      corporateSingleTranscationVol: '',
      corporateMultipleTranscationVol: '',
    },
  });
  const [individualErrorVisible, setIndividualErrorVisible] = useState(false);
  const [corporateErrorVisible, setCorporateErrorVisible] = useState(false);
  const [submitDisabled, setSubmitDisabled] = useState(false);

  const individualClientCalculate = watch('individualClientCalculate');
  const corporateClientCalculate = watch('corporateClientCalculate');

  const fields = watch();

  const [getFactors] = useLazyGetFactorsQuery();

  const { transactionVolumes, baseCurrency } = useAppSelector(
    (state) => state.transactionsRisk.volumesRisk.list,
  );

  const { activePermissions, factors, currenciesList } = useAppSelector(
    (state) => state.amlDictionaries,
  );

  const [getTransactionVolumeRisk, { isLoading }] =
    useLazyGetTransactionVolumesQuery();
  const [getAmlBaseCurrency, { isLoading: baseCurrencyIsLoading }] =
    useLazyGetAmlBaseCurrencyQuery();

  const [updateFactors, { isLoading: requestInProgress }] =
    useUpdateFactorMutation();

  const currentFactors = {
    factorsForIndividualClients: {
      calculate: factors?.find(
        (item) => item.type === RiskScoreType.PERSONAL_TRANSACTION,
      ),
      singleTransaction: factors?.find(
        (item) => item.type === RiskScoreType.PERSONAL_SINGLE_TRANSACTION,
      ),
      multipleTransaction: factors?.find(
        (item) => item.type === RiskScoreType.PERSONAL_MULTIPLE_TRANSACTIONS,
      ),
    },
    factorsForCorporateClients: {
      calculate: factors?.find(
        (item) => item.type === RiskScoreType.CORPORATE_TRANSACTION,
      ),
      singleTransaction: factors?.find(
        (item) => item.type === RiskScoreType.CORPORATE_SINGLE_TRANSACTION,
      ),
      multipleTransaction: factors?.find(
        (item) => item.type === RiskScoreType.CORPORATE_MULTIPLE_TRANSACTIONS,
      ),
    },
  };

  const onFormSubmit = handleSubmit((formValues) => {
    const personalTransactionCalculateBody = {
      ...currentFactors.factorsForIndividualClients.calculate,
      calculationType: formValues.individualClientCalculate,
    };
    const personalSingleTransanctionCalculateBody = {
      ...currentFactors.factorsForIndividualClients.singleTransaction,
      weight:
        individualClientCalculate !== RiskScoreCalculationType.WEIGHT
          ? 0
          : Number(formValues.individualSingleTranscationVol),
    };
    const personalMultipleTransanctionCalculateBody = {
      ...currentFactors.factorsForIndividualClients.multipleTransaction,
      weight:
        individualClientCalculate !== RiskScoreCalculationType.WEIGHT
          ? 0
          : Number(formValues.individualMultipleTranscationVol),
    };

    const corporateTransactionCalculateBody = {
      ...currentFactors.factorsForCorporateClients.calculate,
      calculationType: formValues.corporateClientCalculate,
    };
    const corporatelSingleTransanctionCalculateBody = {
      ...currentFactors.factorsForCorporateClients.singleTransaction,
      weight:
        corporateClientCalculate !== RiskScoreCalculationType.WEIGHT
          ? 0
          : Number(formValues.corporateSingleTranscationVol),
    };
    const corporatelMultipleTransanctionCalculateBody = {
      ...currentFactors.factorsForCorporateClients.multipleTransaction,
      weight:
        corporateClientCalculate !== RiskScoreCalculationType.WEIGHT
          ? 0
          : Number(formValues.corporateMultipleTranscationVol),
    };

    const individualWeightSum =
      personalSingleTransanctionCalculateBody.weight +
      personalMultipleTransanctionCalculateBody.weight;

    const corporateWeightSum =
      corporatelSingleTransanctionCalculateBody.weight +
      corporatelMultipleTransanctionCalculateBody.weight;

    if (
      personalTransactionCalculateBody.calculationType ===
        RiskScoreCalculationType.WEIGHT &&
      individualWeightSum !== 1
    ) {
      setIndividualErrorVisible(true);
    } else {
      setIndividualErrorVisible(false);
    }

    if (
      corporateTransactionCalculateBody.calculationType ===
        RiskScoreCalculationType.WEIGHT &&
      corporateWeightSum !== 1
    ) {
      setCorporateErrorVisible(true);
    } else {
      setCorporateErrorVisible(false);
    }

    if (!submitDisabled) {
      setIndividualErrorVisible(false);
      setCorporateErrorVisible(false);

      Promise.all([
        updateFactors({
          id: currentFactors?.factorsForIndividualClients.calculate
            ?.id as string,
          body: personalTransactionCalculateBody as Factor,
        }),
        updateFactors({
          id: currentFactors?.factorsForIndividualClients?.singleTransaction
            ?.id as string,
          body: personalSingleTransanctionCalculateBody as Factor,
        }),
        updateFactors({
          id: currentFactors?.factorsForIndividualClients?.multipleTransaction
            ?.id as string,
          body: personalMultipleTransanctionCalculateBody as Factor,
        }),
        updateFactors({
          id: currentFactors?.factorsForCorporateClients?.calculate
            ?.id as string,
          body: corporateTransactionCalculateBody as Factor,
        }),
        updateFactors({
          id: currentFactors?.factorsForCorporateClients?.singleTransaction
            ?.id as string,
          body: corporatelSingleTransanctionCalculateBody as Factor,
        }),
        updateFactors({
          id: currentFactors?.factorsForCorporateClients?.multipleTransaction
            ?.id as string,
          body: corporatelMultipleTransanctionCalculateBody as Factor,
        }),
      ]);
    }
  });

  useEffect(() => {
    if (
      individualClientCalculate === RiskScoreCalculationType.WEIGHT &&
      Number(fields.individualSingleTranscationVol) +
        Number(fields.individualMultipleTranscationVol) !==
        1
    ) {
      setSubmitDisabled(true);
    } else if (
      corporateClientCalculate === RiskScoreCalculationType.WEIGHT &&
      Number(fields.corporateSingleTranscationVol) +
        Number(fields.corporateMultipleTranscationVol) !==
        1
    ) {
      setSubmitDisabled(true);
    } else {
      setSubmitDisabled(false);
    }
  }, [individualClientCalculate, fields, corporateClientCalculate]);

  useEffect(() => {
    if (
      currentFactors.factorsForCorporateClients.calculate &&
      currentFactors.factorsForCorporateClients.multipleTransaction &&
      currentFactors.factorsForCorporateClients.singleTransaction &&
      currentFactors.factorsForIndividualClients.calculate &&
      currentFactors.factorsForIndividualClients.multipleTransaction &&
      currentFactors.factorsForIndividualClients.singleTransaction &&
      !individualClientCalculate &&
      !corporateClientCalculate
    ) {
      const body = {
        individualClientCalculate:
          currentFactors.factorsForIndividualClients.calculate.calculationType,
        individualSingleTranscationVol:
          currentFactors.factorsForIndividualClients.singleTransaction.weight,
        individualMultipleTranscationVol:
          currentFactors.factorsForIndividualClients.multipleTransaction.weight,
        corporateClientCalculate:
          currentFactors.factorsForCorporateClients.calculate.calculationType,
        corporateSingleTranscationVol:
          currentFactors.factorsForCorporateClients.singleTransaction.weight,
        corporateMultipleTranscationVol:
          currentFactors.factorsForCorporateClients.multipleTransaction.weight,
      };

      reset(body);
    }
  }, [individualClientCalculate, corporateClientCalculate, currentFactors]);

  useEffect(() => {
    getAmlBaseCurrency();
    getTransactionVolumeRisk();
  }, []);

  useEffect(() => {
    if (!factors) {
      getFactors();
    }
  }, [factors]);

  let content: React.JSX.Element;

  if (isLoading || baseCurrencyIsLoading) {
    content = (
      <div className={cn.transactionsVolumeRiskLoader}>
        <IconLoader />
      </div>
    );
  } else if (
    !isLoading &&
    !baseCurrencyIsLoading &&
    activePermissions?.includes(
      PermissionEnum.SETTINGS_TRANSACTIONS_VOLUMES_LIST,
    )
  ) {
    content = (
      <>
        <VolumesRiskTransactionsForm
          control={control}
          individualClientCalculate={individualClientCalculate}
          corporateClientCalculate={corporateClientCalculate}
          onSubmit={onFormSubmit}
          requestInProgress={requestInProgress}
          isEditable={activePermissions?.includes(
            PermissionEnum.SETTINGS_FACTORS_ACTION,
          )}
          individualErrorVisible={individualErrorVisible}
          corporateErrorVisible={corporateErrorVisible}
        />
        {activePermissions?.includes(
          PermissionEnum.SETTINGS_TRANSACTIONS_VOLUMES_CREATE_ACTION,
        ) && (
          <NavLink
            to={ROUTES.TRANSACTIONS_RISK_VOLUME_RISK_CREATE}
            className={cn.transactionsVolumesRiskLink}
          >
            <IconExpand />
            Add risk group
          </NavLink>
        )}

        <VolumesRiskTransactionsList
          transationVolumes={transactionVolumes}
          baseCurrency={baseCurrency}
          currencies={currenciesList}
        />
      </>
    );
  } else {
    content = <PermissionDenied />;
  }

  return (
    <div className={cn.transactionsVolumesRisk}>
      {content}
      <Outlet />
    </div>
  );
};

export default TransactionVolumesRisk;
